import {
  Button,
  ButtonList,
  DropdownButton,
  DropdownButtonProps,
  FieldWrapper,
  FieldWrapperProps,
  Select,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { NotificationType, useNotifications } from 'hooks/useNotifications';

import { AudioT } from '../audio/api/types';
import { AudioPlayer } from '../audioPlayer/audioPlayer';

import { AdapterTypes, getAdapter, Item } from './adapters';
import { AnnouncementItem } from './adapters/announcementAdapter';
import styles from './announcementPicker.module.scss';
import { RecordDialog } from './recordDialog';
import { UploadDialog } from './uploadDialog';

export type UploadCallback = (file: File) => void;

interface AnnouncementProps {
  adapter: AdapterTypes.Announcement;
  onSelect: (item: AnnouncementItem, triggerType: TriggerType) => void;
  selectedAudio?: AnnouncementItem | null;
}

interface WaitMusicProps {
  adapter: AdapterTypes.WaitMusic;
  onSelect: (item: AudioT, type: TriggerType) => void;
  selectedAudio?: AudioT | null;
}

type CallbackItem = AnnouncementItem & AudioT;
export type TriggerType = 'record' | 'select' | 'upload';

export type Props = PropsWithClassName & {
  adapter: AdapterTypes;
  audioPlayerClassName?: string;
  clearable?: boolean;
  dropdownProps?: DropdownButtonProps;
  fieldProps?: FieldWrapperProps;
  withRecord?: boolean;
  disabled?: boolean;
  onClear?: VoidFunction;
  onDialogChange?: (dialog: Dialog) => void;
  selectedAudio?: unknown;
  onSelect: unknown;
} & (AnnouncementProps | WaitMusicProps);

export enum Dialog {
  Default = 'default',
  Record = 'record',
  Upload = 'upload',
}

type UploadType = Dialog.Upload | Dialog.Record;

export function AnnouncementPicker({
  adapter: adapterName,
  audioPlayerClassName,
  clearable,
  className,
  dropdownProps,
  fieldProps,
  selectedAudio: passedSelectedAudio,
  withRecord = true,
  disabled,
  onClear,
  onDialogChange,
  onSelect,
}: Props): JSX.Element {
  const adapter = useMemo(() => getAdapter(adapterName), [adapterName]);
  const [isLoading, setIsLoading] = useState(false);
  const [audioItems, setAudioItems] = useState<Item[]>([]);
  const initialSelectedAudio = useMemo(() => {
    if (!adapter || !passedSelectedAudio || audioItems.length === 0) {
      return undefined;
    }

    return adapter.getFormattedItem(passedSelectedAudio);
  }, [adapter, passedSelectedAudio, audioItems]);
  const [selectedAudio, setSelectedAudio] = useState<Item | null>(
    initialSelectedAudio ?? null,
  );
  const [dialog, setDialog] = useState<Dialog>(Dialog.Default);
  const addNotification = useNotifications();

  useUpdateEffect(() => {
    onDialogChange?.(dialog);
  }, [dialog, onDialogChange, useUpdateEffect]);

  const handleAudioSelect = useCallback(
    ({ id }) => {
      const selectedAudio = audioItems?.find((audio) => audio.id === id);

      if (!selectedAudio) {
        return;
      }

      setSelectedAudio(selectedAudio);

      const item = adapter.getItemForCallback(id);

      if (item) {
        onSelect(item as CallbackItem, 'select');
      }
    },
    [audioItems, setSelectedAudio, onSelect, adapter],
  );

  const handleClear = useCallback(() => {
    setSelectedAudio(null);
    onClear?.();
  }, [onClear]);

  const handleUpload = useCallback<
    (type: UploadType) => (file: File) => Promise<void>
  >(
    (type: UploadType) => async (file) => {
      setIsLoading(true);

      try {
        const url = await adapter.upload(file);
        const items = await adapter.getItems();
        const uploadedItem = adapter.getItemFromURL(url);

        setAudioItems(items);

        if (!uploadedItem) {
          return;
        }

        const callbackItem = adapter.getItemForCallback(uploadedItem.id);

        if (callbackItem) {
          onSelect(
            callbackItem as CallbackItem,
            type === Dialog.Upload ? 'upload' : 'record',
          );
        }

        setSelectedAudio(uploadedItem);
      } catch (err) {
        console.error(
          'There was an error while trying to upload the audio file',
          err,
        );

        addNotification(
          { text: 'An error occurred when trying to upload the file' },
          NotificationType.Error,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [adapter, setIsLoading, setAudioItems, addNotification, onSelect],
  );

  const onClose = useCallback(() => setDialog(Dialog.Default), []);
  const onRecordClick = useCallback(() => setDialog(Dialog.Record), []);
  const onUploadClick = useCallback(() => setDialog(Dialog.Upload), []);

  const isDefaultOpen = dialog === Dialog.Default;
  const isRecordOpen = dialog === Dialog.Record;
  const isUploadOpen = dialog === Dialog.Upload;

  useEffect(() => {
    if (!initialSelectedAudio) {
      return;
    }

    setSelectedAudio(initialSelectedAudio);
  }, [initialSelectedAudio, setSelectedAudio]);

  useEffect(() => {
    const handleGetAudio = async () => {
      setIsLoading(true);

      try {
        const items = await adapter.getItems();

        setAudioItems(items);
      } catch (err) {
        console.error('Failed to fetch audio items', err);

        addNotification(
          { text: 'Failed to fetch audio items, please try again' },
          NotificationType.Error,
        );
      } finally {
        setIsLoading(false);
      }
    };

    handleGetAudio();
  }, [adapter, adapterName, addNotification]);

  return (
    <div className={className}>
      {isRecordOpen && (
        <RecordDialog
          onClose={onClose}
          onUpload={handleUpload(Dialog.Record)}
        />
      )}
      {isUploadOpen && (
        <UploadDialog
          onClose={onClose}
          onUpload={handleUpload(Dialog.Upload)}
        />
      )}
      {isDefaultOpen && (
        <FieldWrapper label="Select an audio file" {...fieldProps}>
          <div className={styles.select}>
            <Select
              clearable={clearable}
              loading={isLoading}
              disabled={disabled || isLoading}
              dropdownProps={dropdownProps}
              placeholder="No audio selected"
              selectedItems={selectedAudio ? [selectedAudio?.id] : []}
              items={audioItems}
              onClear={handleClear}
              onSelect={handleAudioSelect}
              searchable
            />
            {withRecord ? (
              <DropdownButton
                className={styles.dropdown}
                buttonProps={{ variant: 'secondary', disabled }}
                dropdown={
                  <ButtonList>
                    <ButtonList.Item
                      buttonProps={{ type: 'button' }}
                      onClick={onRecordClick}
                    >
                      Record
                    </ButtonList.Item>
                    <ButtonList.Item
                      buttonProps={{ type: 'button' }}
                      onClick={onUploadClick}
                    >
                      Upload file
                    </ButtonList.Item>
                  </ButtonList>
                }
              >
                New audio file
              </DropdownButton>
            ) : (
              <Button onClick={onUploadClick} size="large" variant="secondary">
                Upload file
              </Button>
            )}
          </div>
          <AudioPlayer
            isDownloadable
            className={classNames(styles.audioPlayer, audioPlayerClassName)}
            disabled={isLoading}
            src={selectedAudio?.url}
          />
        </FieldWrapper>
      )}
    </div>
  );
}
